/**
 * Required fields left empty in a form, found from the form's own markup: a `ui5-label[required]` names its editor with
 * `for`. Used to say which fields a "Some fields need your attention" answer is about, on any dialog or object page,
 * without each screen mapping the server's field paths to its labels.
 */
export interface MissingField {
  label: string;
  element: HTMLElement;
}

const valueOf = (element: HTMLElement): string => {
  if (element.tagName === "UI5-SELECT") {
    // A Select reports the text of an option whose value is "" as its value: read the option's attribute instead.
    const selected = (element as HTMLElement & { selectedOption?: HTMLElement | null }).selectedOption;
    return selected?.getAttribute("value") ?? selected?.textContent ?? "";
  }
  const value = (element as HTMLElement & { value?: unknown }).value;
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
};

/** The empty required fields under `root`, in page order. */
export function missingRequiredFields(root: ParentNode): MissingField[] {
  const missing: MissingField[] = [];
  for (const label of root.querySelectorAll<HTMLElement>("ui5-label[required][for]")) {
    const id = label.getAttribute("for") ?? "";
    const element = id === "" ? null : root.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`);
    if (!element || !element.tagName.startsWith("UI5-") || element.hasAttribute("disabled")) continue;
    if (element.hasAttribute("readonly")) continue;
    // A closed dialog on the page (e.g. a reversal reason) is not part of the form that was saved.
    const dialog = element.closest("ui5-dialog");
    if (dialog && dialog !== root && !dialog.hasAttribute("open")) continue;
    if (valueOf(element).trim() !== "") continue;
    missing.push({ label: label.textContent.trim().replace(/[:：]\s*$/u, ""), element });
  }
  return missing;
}

/** Marks the fields in error until the user changes them. */
export function markMissing(fields: readonly MissingField[]): void {
  for (const { element } of fields) {
    element.setAttribute("value-state", "Negative");
    const clear = () => {
      element.setAttribute("value-state", "None");
      element.removeEventListener("input", clear);
      element.removeEventListener("change", clear);
    };
    element.addEventListener("input", clear);
    element.addEventListener("change", clear);
  }
}

/**
 * The field a refused value belongs to, from the error's `args.field` (and `args.role` on a party's role terms): the
 * editor whose id ends with "-<field>" (the forms' id convention, e.g. `party-vatPercentage`,
 * `party-role-customer-creditLimit`), with its label. `null` when the form has no such editor.
 */
export function fieldInError(root: ParentNode, field: string, scope?: string): MissingField | null {
  if (!/^[A-Za-z][A-Za-z0-9]*$/u.test(field)) return null;
  const candidates = [...root.querySelectorAll<HTMLElement>(`[id$="-${field}"]`)].filter((element) =>
    element.tagName.startsWith("UI5-"),
  );
  const element =
    (scope ? candidates.find((candidate) => candidate.id.includes(`-${scope}-`)) : undefined) ?? candidates[0];
  if (!element) return null;
  const label = root.querySelector<HTMLElement>(`ui5-label[for="${CSS.escape(element.id)}"]`);
  return { label: label?.textContent.trim().replace(/[:：]\s*$/u, "") ?? "", element };
}

/**
 * The fields a refused save names (`args.field`, or `args.errors[].field`), found by the `data-api-field` attribute a form
 * puts around an editor: the error then points at the field the server rejected, not at another field that only looks
 * empty (e.g. one inheriting its value).
 */
export function namedFields(root: ParentNode, args: Readonly<Record<string, unknown>>): MissingField[] {
  const names = new Set<string>();
  if (typeof args["field"] === "string") names.add(args["field"]);
  const errors = args["errors"];
  if (Array.isArray(errors)) {
    for (const item of errors as unknown[]) {
      const field = typeof item === "object" && item !== null ? (item as Record<string, unknown>)["field"] : undefined;
      if (typeof field === "string") names.add(field);
    }
  }
  const found: MissingField[] = [];
  for (const name of names) {
    const host = root.querySelector<HTMLElement>(`[data-api-field="${CSS.escape(name)}"]`);
    const element = host?.querySelector<HTMLElement>("[id]");
    if (!element) continue;
    const label = root.querySelector<HTMLElement>(`ui5-label[for="${CSS.escape(element.id)}"]`);
    found.push({ label: label?.textContent.trim().replace(/[:：]\s*$/u, "") ?? name, element });
  }
  return found;
}
